import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@produzionejb/ui';
import { useCustomers, useDeleteCustomer } from '../../lib/customers-api';

const PAGE_SIZE = 50;

export function CustomerListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [country, setCountry] = useState('');
  const [activeFilter, setActiveFilter] = useState<'' | 'true' | 'false'>('');
  const [page, setPage] = useState(1);

  function handleSearchChange(v: string) {
    setSearch(v);
    setPage(1);
    window.clearTimeout((handleSearchChange as unknown as { t: number }).t);
    (handleSearchChange as unknown as { t: number }).t = window.setTimeout(() => {
      setDebouncedSearch(v.trim());
    }, 300);
  }

  const { data, isLoading, error } = useCustomers({
    page,
    pageSize: PAGE_SIZE,
    ...(debouncedSearch && { search: debouncedSearch }),
    ...(country.length === 2 && { country: country.toUpperCase() }),
    ...(activeFilter && { active: activeFilter === 'true' }),
  });

  const deleteMut = useDeleteCustomer();

  async function handleDelete(id: string, name: string) {
    if (!window.confirm(t('customers.list.confirmDelete', { name }))) return;
    try {
      await deleteMut.mutateAsync(id);
    } catch (err) {
      window.alert((err as Error).message);
    }
  }

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-slate-900">{t('customers.list.title')}</h2>
        <Button onClick={() => navigate('/customers/new')}>{t('customers.list.newButton')}</Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('products.list.filtersTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-4">
          <div>
            <label className="text-xs text-slate-500">{t('customers.list.searchLabel')}</label>
            <Input
              placeholder={t('customers.list.searchPlaceholder')}
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs text-slate-500">{t('customers.fields.country')}</label>
            <Input
              placeholder="IT, CZ, DE..."
              maxLength={2}
              value={country}
              onChange={(e) => {
                setCountry(e.target.value.toUpperCase());
                setPage(1);
              }}
            />
          </div>
          <div>
            <label className="text-xs text-slate-500">{t('customers.fields.active')}</label>
            <select
              className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              value={activeFilter}
              onChange={(e) => {
                setActiveFilter(e.target.value as '' | 'true' | 'false');
                setPage(1);
              }}
            >
              <option value="">{t('common.all')}</option>
              <option value="true">{t('customers.activeYes')}</option>
              <option value="false">{t('customers.activeNo')}</option>
            </select>
          </div>
          <div className="flex items-end">
            <span className="text-sm text-slate-600">
              {data ? t('customers.list.totalCount', { count: data.total }) : ''}
            </span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading && <p className="p-4 text-sm text-slate-500">{t('common.loading')}</p>}
          {error && (
            <p className="p-4 text-sm text-red-600">
              {t('customers.list.errorLoad')}: {(error as Error).message}
            </p>
          )}
          {data && data.items.length === 0 && (
            <p className="p-4 text-sm text-slate-500">{t('customers.list.empty')}</p>
          )}
          {data && data.items.length > 0 && (
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase text-slate-600">
                <tr>
                  <th className="px-4 py-2">{t('customers.fields.code')}</th>
                  <th className="px-4 py-2">{t('customers.fields.name')}</th>
                  <th className="px-4 py-2">{t('customers.fields.country')}</th>
                  <th className="px-4 py-2">{t('customers.fields.vatNumber')}</th>
                  <th className="px-4 py-2">{t('customers.fields.email')}</th>
                  <th className="px-4 py-2">{t('customers.fields.active')}</th>
                  <th className="px-4 py-2 text-right">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((c) => (
                  <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-2 font-mono text-xs">{c.code}</td>
                    <td className="px-4 py-2 font-medium text-slate-900">{c.name}</td>
                    <td className="px-4 py-2 text-slate-700">{c.country}</td>
                    <td className="px-4 py-2 font-mono text-xs text-slate-700">
                      {c.vatNumber ?? '—'}
                    </td>
                    <td className="px-4 py-2 text-slate-700">{c.email ?? '—'}</td>
                    <td className="px-4 py-2">
                      <span
                        className={
                          c.active
                            ? 'rounded bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700'
                            : 'rounded bg-slate-200 px-2 py-0.5 text-xs text-slate-600'
                        }
                      >
                        {c.active ? t('customers.activeYes') : t('customers.activeNo')}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Link
                        to={`/customers/${c.id}/edit`}
                        className="mr-3 text-brand-700 hover:underline"
                      >
                        {t('common.edit')}
                      </Link>
                      <button
                        type="button"
                        onClick={() => handleDelete(c.id, c.name)}
                        className="text-red-600 hover:underline"
                        disabled={deleteMut.isPending}
                      >
                        {t('common.delete')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {data && totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            ‹
          </Button>
          <span className="text-sm text-slate-600">
            {t('products.list.pageOf', { page, totalPages })}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            ›
          </Button>
        </div>
      )}
    </div>
  );
}
